import { ArrowRightIcon, BoxIcon } from "@xola/icons";
import { get } from "lodash";
import React from "react";
import { Audit, LineItem, LineItemType, Purchase, PurchaseItem } from "types";
import { getAddonLineItems } from "../../../../models/PurchaseItem";
import { NoTranslate } from "../../../Common/NoTranslate";
import { TimelineItem, TimelinePill } from "../TimelineItem";

interface AddOnTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const AddOnTimelineItem = ({ purchase, item, audit, showDate = false }: AddOnTimelineItemProps) => {
    const logs = getLog(item, audit);
    if (!AddOnTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Add-ons Changed"
            audit={audit}
            icon={<BoxIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="flex items-center space-x-4">
                <TimelinePill>
                    <div className="flex flex-col">
                        {logs.map(({ from }: any) => {
                            const [log] = from;
                            return <LogItem key={log?.id} log={log} field="from" />;
                        })}
                    </div>
                </TimelinePill>
                <ArrowRightIcon />
                <TimelinePill>
                    <div className="flex flex-col">
                        {logs.map(({ to }: any) => {
                            const [log] = to;
                            return <LogItem key={log?.id} log={log} field="to" />;
                        })}
                    </div>
                </TimelinePill>
            </div>
        </TimelineItem>
    );
};

AddOnTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const logs = getLog(item, audit);
    return logs.length;
};

const getLog = (item: PurchaseItem, audit: Audit) => {
    const purchaseAddons = getAddonLineItems(item);

    // These checks are needed to determine the correct audit structure (lineItems, guests, amount, id).
    // Some audits may have an incorrect structure and this cause a bug with unnecessary timeline items to appear.
    const isValidAuditStructure =
        audit.data?.items &&
        audit.data?.items.length &&
        audit.data?.items[0]?.lineItems &&
        audit.data?.items[0]?.guests &&
        audit.data?.items[0]?.amount;

    if (isValidAuditStructure) {
        const groupLogs = Object.values(audit.data?.items[0]?.lineItems)
            .filter((log: any) => !(Object.values(log).length === 2 && "id" in log && "sequence" in log))
            .map((log: any) => {
                const from = [];
                const to = [];

                if ([LineItemType.ADDON].includes(log?.old?.type) || [LineItemType.ADDON].includes(log?.new?.type)) {
                    if (log?.id) {
                        const itemData = item.lineItems.find((item: LineItem) => item.id === log.id);

                        from.push({ name: itemData?.name, from: log.quantity.old });
                        to.push({ name: itemData?.name, to: log.quantity.new });
                    }

                    if (log?.old || log?.new) {
                        from.push(log.old ? { name: log.new?.name, from: log.old?.quantity } : null);
                        to.push(log.new ? { name: log.new?.name, to: log.new?.quantity } : null);
                    }

                    return {
                        from,
                        to,
                    };
                }

                if (purchaseAddons.some((addon) => addon.id === log.id)) {
                    const itemData = purchaseAddons.find((addon) => addon.id === log.id);

                    from.push({ name: itemData?.name, from: log.quantity?.old });
                    to.push({ name: itemData?.name, to: log.quantity?.new });

                    return {
                        from,
                        to,
                    };
                }

                return false;
            })
            .filter(Boolean);
        return groupLogs ?? [];
    }

    return [];
};

interface LogItemProps {
    log: {
        name: string;
        quantity: number;
    };
    field: string;
}

const LogItem = ({ log, field }: LogItemProps) => {
    if (log) {
        const value = get(log, `${field}`, false);

        return (
            <TimelinePill>
                <NoTranslate className="whitespace-normal leading-4.5">
                    {value === null ? log.quantity : value} x {log.name}
                </NoTranslate>
            </TimelinePill>
        );
    }

    return <TimelinePill>No Add-ons</TimelinePill>;
};
